/**
 * Feature codes for the feature gating system.
 * These codes must match the backend FeatureCodes constants
 * (OpenTalents.Domain.Entities.Subscriptions.SubscriptionPlanFeature.FeatureCodes).
 *
 * @userstory US-FG-01, US-FG-04, US-FG-05, US-FG-06, US-FG-07, US-FG-08, US-FG-09, US-FG-11, US-FG-12, US-FG-13, US-SUB-02
 */
export const FeatureCodes = {
	// Organization features
	ORGANIZATION_SEND_MESSAGES: 'ORGANIZATION_SEND_MESSAGES',
	ORGANIZATION_AI_SEARCH: 'ORGANIZATION_AI_SEARCH',
	ORGANIZATION_SAVED_SEARCHES: 'ORGANIZATION_SAVED_SEARCHES',
	ORGANIZATION_EXPLORE_SEARCHES: 'ORGANIZATION_EXPLORE_SEARCHES',
	ORGANIZATION_COLLECTION_ITEMS: 'ORGANIZATION_COLLECTION_ITEMS',
	ORGANIZATION_SAVED_SEARCH_PREVIEW: 'ORGANIZATION_SAVED_SEARCH_PREVIEW',
	ORGANIZATION_SAVED_SEARCH_ALERTS: 'ORGANIZATION_SAVED_SEARCH_ALERTS',
	ORGANIZATION_AI_BRIEFS: 'ORGANIZATION_AI_BRIEFS',
	ORGANIZATION_TEAM_SEATS: 'ORGANIZATION_TEAM_SEATS',
	ORGANIZATION_TRUST_CONTROLS: 'ORGANIZATION_TRUST_CONTROLS',
	ORGANIZATION_CHAT_MESSAGES: 'ORGANIZATION_CHAT_MESSAGES',
	ORGANIZATION_CONTRACTS: 'ORGANIZATION_CONTRACTS',
	ORGANIZATION_QUOTES: 'ORGANIZATION_QUOTES',
	ORGANIZATION_EXPLORE_AI_ASSISTANT: 'ORGANIZATION_EXPLORE_AI_ASSISTANT',
	// Future: API/MCP access (enterprise-only, not active until production implementation)
	ORGANIZATION_MCP_SEARCH: 'ORGANIZATION_MCP_SEARCH',

	// Talent features
	TALENT_ACCEPT_CONTRACTS: 'TALENT_ACCEPT_CONTRACTS',
	TALENT_PROFILE_BOOST: 'TALENT_PROFILE_BOOST',
	TALENT_AI_PROFILE_SUGGESTIONS: 'TALENT_AI_PROFILE_SUGGESTIONS',
	TALENT_PEER_SEARCHES: 'TALENT_PEER_SEARCHES',
	TALENT_OPPORTUNITY_RADAR_PREVIEW: 'TALENT_OPPORTUNITY_RADAR_PREVIEW',
	TALENT_REFERRALS: 'TALENT_REFERRALS',
	TALENT_OPPORTUNITY_RADAR: 'TALENT_OPPORTUNITY_RADAR',
	TALENT_INTENT_VISIBILITY: 'TALENT_INTENT_VISIBILITY',
	TALENT_STAKEHOLDER_MESSAGES: 'TALENT_STAKEHOLDER_MESSAGES',
	TALENT_MARKET_POSITIONING: 'TALENT_MARKET_POSITIONING',
	TALENT_VISIBILITY_BOOST: 'TALENT_VISIBILITY_BOOST',
	TALENT_QUOTES: 'TALENT_QUOTES',
	// Media gallery features (storage cost controls)
	TALENT_MEDIA_GALLERY_ITEMS: 'TALENT_MEDIA_GALLERY_ITEMS',
	TALENT_MEDIA_GALLERY_STORAGE: 'TALENT_MEDIA_GALLERY_STORAGE',
} as const;

export type FeatureCode = (typeof FeatureCodes)[keyof typeof FeatureCodes];

/**
 * Feature types
 */
export type FeatureType = 'boolean' | 'limit';

/**
 * Plan types matching backend SubscriptionPlanTypes.
 * Used to identify the user's current subscription tier.
 *
 * @userstory US-FG-01, US-FG-09
 */
export const PlanTypes = {
	// Organization plans
	ORGANIZATION_FREE: 'ORGANIZATION_FREE',
	ORGANIZATION_PLUS: 'ORGANIZATION_PLUS',
	ORGANIZATION_PRO: 'ORGANIZATION_PRO',

	// Talent plans
	TALENT_FREE: 'TALENT_FREE',
	TALENT_PLUS: 'TALENT_PLUS',
	TALENT_PRO: 'TALENT_PRO',
} as const;

export type PlanType = (typeof PlanTypes)[keyof typeof PlanTypes];

/**
 * Feature display names for UI rendering.
 * Used by FeatureErrorModal, UpgradePrompt, and other components
 * to show human-readable feature names.
 *
 * @userstory US-FG-02, US-FG-10
 */
export const FeatureNames: Record<FeatureCode, string> = {
	ORGANIZATION_SEND_MESSAGES: 'Send Messages',
	ORGANIZATION_AI_SEARCH: 'AI Search',
	ORGANIZATION_SAVED_SEARCHES: 'Saved Searches',
	ORGANIZATION_EXPLORE_SEARCHES: 'Explore Searches',
	ORGANIZATION_COLLECTION_ITEMS: 'Collection Items',
	ORGANIZATION_SAVED_SEARCH_PREVIEW: 'Saved Search Preview',
	ORGANIZATION_SAVED_SEARCH_ALERTS: 'Saved Search Alerts',
	ORGANIZATION_AI_BRIEFS: 'AI Brief Builder',
	ORGANIZATION_TEAM_SEATS: 'Team Seats',
	ORGANIZATION_TRUST_CONTROLS: 'Trust Controls',
	ORGANIZATION_CHAT_MESSAGES: 'Chat Messages',
	ORGANIZATION_CONTRACTS: 'Contracts',
	ORGANIZATION_QUOTES: 'Quotes',
	ORGANIZATION_EXPLORE_AI_ASSISTANT: 'Explore AI Assistant',
	ORGANIZATION_MCP_SEARCH: 'API / MCP Search',
	TALENT_ACCEPT_CONTRACTS: 'Accept Contracts',
	TALENT_PROFILE_BOOST: 'Profile Boost',
	TALENT_AI_PROFILE_SUGGESTIONS: 'AI Profile Suggestions',
	TALENT_PEER_SEARCHES: 'Peer Searches',
	TALENT_OPPORTUNITY_RADAR_PREVIEW: 'Opportunity Radar Preview',
	TALENT_REFERRALS: 'Referrals',
	TALENT_OPPORTUNITY_RADAR: 'Opportunity Radar',
	TALENT_INTENT_VISIBILITY: 'Company Intent Visibility',
	TALENT_STAKEHOLDER_MESSAGES: 'Stakeholder Messaging',
	TALENT_MARKET_POSITIONING: 'Market Positioning',
	TALENT_VISIBILITY_BOOST: 'Visibility Boost',
	TALENT_QUOTES: 'Quotes',
	TALENT_MEDIA_GALLERY_ITEMS: 'Media Gallery Items',
	TALENT_MEDIA_GALLERY_STORAGE: 'Media Gallery Storage',
};
